// premium-service-detail: complete template for all service pages
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Clock, Users, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface ServiceDetailPageProps {
  service?: {
    name: string;
    promise: string;
    primaryKPI: string;
    signals: {
      bestFor: string;
      timeline: string;
      team: string;
      deliverables: string;
    };
    outputs: string[];
    process: {
      step: string;
      description: string;
      ownership: string;
      artifacts: string[];
    }[];
    proof: {
      client: string;
      challenge: string;
      result: string;
      metric: string;
    }[];
    faqs: {
      question: string;
      answer: string;
    }[];
  };
}

// Website Development service data
const websiteService = {
  name: "Website Development",
  promise: "Fast, conversion-focused websites that grow your business",
  primaryKPI: "+47% average conversion lift",
  signals: {
    bestFor: "Growing companies who need more qualified leads",
    timeline: "2-4 weeks to launch",
    team: "Senior designer + developer",
    deliverables: "Custom site + performance optimization"
  },
  outputs: [
    "Mobile-first responsive design",
    "Core Web Vitals optimization (90+ scores)",
    "Conversion tracking setup",
    "Content management system",
    "SEO foundation (meta, schema, sitemap)",
    "Analytics dashboard",
    "2 weeks post-launch support"
  ],
  process: [
    {
      step: "Discovery & Strategy",
      description: "Understand your goals, audience, and success metrics",
      ownership: "We lead, you provide business context",
      artifacts: ["Site map", "User flow", "Technical requirements"]
    },
    {
      step: "Design & Prototype",
      description: "Create high-fidelity designs focused on conversion",
      ownership: "We design, you approve key pages",
      artifacts: ["Desktop/mobile designs", "Interactive prototype", "Style guide"]
    },
    {
      step: "Development & Testing",
      description: "Build with performance and SEO in mind",
      ownership: "We code, you review staging site",
      artifacts: ["Staging site", "Performance report", "QA checklist"]
    },
    {
      step: "Launch & Optimize",
      description: "Deploy and monitor initial performance",
      ownership: "We launch, you provide feedback",
      artifacts: ["Live site", "Analytics setup", "Post-launch report"]
    }
  ],
  proof: [
    {
      client: "SaaS startup (Series A)",
      challenge: "Demo requests were low despite good traffic",
      result: "Redesigned homepage and pricing page flow",
      metric: "+63% demo signups in 30 days"
    },
    {
      client: "Professional services firm",
      challenge: "Site was slow and looked outdated",
      result: "New site with 1.8s load time and modern design",
      metric: "+28% contact form submissions"
    }
  ],
  faqs: [
    {
      question: "What's included in the base price?",
      answer: "Complete website with up to 10 pages, mobile optimization, CMS setup, and analytics. Additional pages are $200 each."
    },
    {
      question: "How long does it take?",
      answer: "Most sites launch within 2-4 weeks. Complex features or integrations may add 1-2 weeks."
    },
    {
      question: "Do you provide hosting?",
      answer: "We set up hosting on your preferred platform (Webflow, WordPress, or custom). Hosting costs are separate."
    },
    {
      question: "What if I need changes after launch?",
      answer: "First 2 weeks of updates are included. After that, changes are billed at $150/hour with 30-minute minimum."
    },
    {
      question: "How do you ensure good performance?",
      answer: "We optimize images, minimize code, use CDN, and target 90+ Core Web Vitals scores. Performance report included."
    },
    {
      question: "Can you work with our existing brand?",
      answer: "Yes, we'll work within your brand guidelines. If you need brand development, that's a separate project."
    }
  ]
};

export const ServiceDetailPage = ({ service = websiteService }: ServiceDetailPageProps) => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background">
      
      {/* Hero */}
      <section className="pt-40 pb-24 bg-gradient-to-b from-background via-background to-background-secondary relative overflow-hidden">
        {/* Subtle pattern background */}
        <div className="absolute inset-0 pattern-grid opacity-[0.015] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-content mx-auto text-center">
            
            <div className="mb-10">
              <div className="micro-label mb-8 opacity-80">{service.name.toUpperCase()}</div>
              <h1 className="font-display font-bold text-foreground mb-8" style={{ fontSize: '64px', lineHeight: '1.05', letterSpacing: '-0.02em' }}>
                {service.promise}
              </h1>
              
              {/* Primary KPI */}
              <div className="inline-flex items-center px-8 py-4 glass-panel shadow-lg shadow-primary/5 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
                <span className="text-primary font-semibold text-xl">{service.primaryKPI}</span>
              </div>
            </div>

            {/* CTA */}
            <Link to="/contact">
              <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all">
                Start your project
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>

          </div>
        </div>
      </section>

      {/* Signals */}
      <section className="py-24 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content-wide mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              
              <div className="glass-panel p-8 text-center hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/5">
                <div className="text-sm text-muted-foreground mb-3 uppercase tracking-wider">Best for</div>
                <div className="font-semibold text-foreground leading-snug">{service.signals.bestFor}</div>
              </div>
              
              <div className="glass-panel p-8 text-center hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/5">
                <Clock className="w-6 h-6 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-sm text-muted-foreground mb-3 uppercase tracking-wider">Timeline</div>
                <div className="font-semibold text-foreground">{service.signals.timeline}</div>
              </div>
              
              <div className="glass-panel p-8 text-center hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/5">
                <Users className="w-6 h-6 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-sm text-muted-foreground mb-3 uppercase tracking-wider">Team</div>
                <div className="font-semibold text-foreground">{service.signals.team}</div>
              </div>
              
              <div className="glass-panel p-8 text-center hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/5">
                <CheckCircle className="w-6 h-6 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-sm text-muted-foreground mb-3 uppercase tracking-wider">Deliverables</div>
                <div className="font-semibold text-foreground">{service.signals.deliverables}</div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 bg-background relative overflow-hidden">
        {/* Subtle pattern background */}
        <div className="absolute inset-0 pattern-grid opacity-[0.015] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-content mx-auto">
            
            <div className="mb-20">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.15', letterSpacing: '-0.01em' }}>
                What you get
              </h2>
              <p className="text-muted-foreground text-lg">Concrete outputs delivered on schedule</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {service.outputs.map((output, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="mt-1">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 drop-shadow-[0_0_8px_hsl(var(--primary)/0.3)]" strokeWidth={2} />
                  </div>
                  <span className="text-foreground text-[17px] leading-relaxed">{output}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="mb-20">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.15', letterSpacing: '-0.01em' }}>
                How it works
              </h2>
              <p className="text-muted-foreground text-lg">Clear steps with defined ownership</p>
            </div>

            <div className="space-y-12 relative">
              {/* Connecting line */}
              <div className="absolute left-5 top-8 bottom-8 w-px bg-gradient-to-b from-primary/30 via-primary/20 to-primary/30 hidden lg:block"></div>
              
              {service.process.map((step, index) => (
                <div key={index} className="glass-panel p-10 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 hover:scale-[1.02] relative">
                  <div className="flex items-start gap-8">
                    
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border-2 border-primary/40 flex items-center justify-center shadow-lg shadow-primary/20">
                        <span className="text-primary font-bold">{index + 1}</span>
                      </div>
                    </div>

                    <div className="flex-1">
                      <h3 className="font-display font-semibold text-foreground mb-3" style={{ fontSize: '24px', letterSpacing: '-0.01em' }}>
                        {step.step}
                      </h3>
                      <p className="text-muted-foreground text-[17px] mb-6 leading-relaxed">{step.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex flex-col gap-2">
                          <span className="text-sm text-muted-foreground uppercase tracking-wider">Ownership</span>
                          <span className="text-foreground font-medium">{step.ownership}</span>
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className="text-sm text-muted-foreground uppercase tracking-wider">Artifacts</span>
                          <span className="text-foreground font-medium">{step.artifacts.join(', ')}</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="py-24 bg-background relative overflow-hidden">
        {/* Subtle pattern background */}
        <div className="absolute inset-0 pattern-grid opacity-[0.015] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-content mx-auto">
            
            <div className="mb-20">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.15', letterSpacing: '-0.01em' }}>
                Real results
              </h2>
              <p className="text-muted-foreground text-lg">Before and after outcomes</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {service.proof.map((case_study, index) => (
                <div key={index} className="glass-panel p-10 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/5 transition-all duration-300">
                  <div className="mb-6">
                    <div className="text-sm text-muted-foreground uppercase tracking-wider mb-3">{case_study.client}</div>
                    <h3 className="font-semibold text-foreground text-lg mb-4 leading-snug">{case_study.challenge}</h3>
                    <p className="text-muted-foreground text-[17px] mb-6 leading-relaxed">{case_study.result}</p>
                  </div>
                  
                  <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary/15 to-primary/5 border border-primary/30 rounded-full shadow-lg shadow-primary/10">
                    <span className="text-primary font-bold text-lg">{case_study.metric}</span>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="mb-20">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.15', letterSpacing: '-0.01em' }}>
                Common questions
              </h2>
              <p className="text-muted-foreground text-lg">Direct answers about scope and process</p>
            </div>

            <div className="space-y-6">
              {service.faqs.map((faq, index) => (
                <div key={index} className="glass-panel overflow-hidden hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
                  <button
                    className="w-full p-8 text-left flex items-center justify-between hover:bg-glass-bg/50 transition-colors group"
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  >
                    <span className="font-semibold text-foreground text-lg pr-4 group-hover:text-primary transition-colors">{faq.question}</span>
                    {expandedFAQ === index ? (
                      <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={2} />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 group-hover:text-primary transition-colors" strokeWidth={2} />
                    )}
                  </button>
                  
                  {expandedFAQ === index && (
                    <div className="px-8 pb-8 animate-accordion-down">
                      <div className="h-px bg-gradient-to-r from-transparent via-stroke-hairline to-transparent mb-6" />
                      <p className="text-muted-foreground text-[17px] leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-b from-background to-background-secondary relative overflow-hidden">
        {/* Subtle pattern background */}
        <div className="absolute inset-0 pattern-grid opacity-[0.02] pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-content mx-auto text-center">
            
            <h2 className="font-display font-bold text-foreground mb-8" style={{ fontSize: '48px', lineHeight: '1.1', letterSpacing: '-0.02em' }}>
              Ready to start your project?
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-paragraph mx-auto leading-relaxed">
              Book a 15-minute call to discuss your goals and timeline.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/35 transition-all hover:scale-105">
                  Book a call
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground tap-target hover:bg-glass-bg">
                  Send details instead
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};